import { Component, inject } from '@angular/core';
import { ComponentsModule, DialogService, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ClientService } from '@lib/client/client.service';
import { Client, createClientDetailsFormGroup, ClientSingleResponse, StoreClientRequest } from '@lib/api/models/client';
import { createPersonDetailsFormGroup } from '@lib/api/models/person';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { ReactiveFormsModule } from '@angular/forms';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';

@Component({
    selector: 'app-edit-client-general-information-dialog',
    imports: [
        ComponentsModule,
        ErrorPanelComponent,
        ReactiveFormsModule
    ],
    templateUrl: './edit-client-general-information-dialog.component.html',
    standalone: true,
    styleUrl: './edit-client-general-information-dialog.component.scss'
})
export class EditClientGeneralInformationDialogComponent {

    private readonly _clientService = inject(ClientService);
    private readonly _snackBar = inject(SnackBarService);

    protected readonly client = inject<Client>(DIALOG_DATA);
    protected readonly clientDetailsFormGroup = createClientDetailsFormGroup(this.client);
    protected readonly personDetailsFormGroup = createPersonDetailsFormGroup(this.client.person);
    protected readonly dialogRef = inject<DialogRef>(DialogRef);

    updateClientRequest = new SendableRequest<ClientSingleResponse, StoreClientRequest>({
        request: (payload) => this._clientService.updateClient(this.client.id, payload!),

        onSend: () => {
            this.personDetailsFormGroup.disable();
        },

        onSuccess: () => {
            this.personDetailsFormGroup.enable();
            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'General Information Updated'
            });
            this.dialogRef.close();
        },

        onError: () => {
            this.personDetailsFormGroup.enable();
        }
    });

    update() {
        if (this.updateClientRequest.loading()) {
            return;
        }

        const clientData: StoreClientRequest = {
            reference: this.clientDetailsFormGroup.get('reference')?.value ?? '',
            nhs_number: this.clientDetailsFormGroup.get('nhs_number')?.value ?? '',
            email: this.client.email,

            person: {
                additional_comments: this.client.person.additional_comments,
                title: this.personDetailsFormGroup.get('title')?.value ?? '',
                given_name: this.personDetailsFormGroup.get('given_name')?.value ?? '',
                middle_names: this.personDetailsFormGroup.get('middle_names')?.value ?? '',
                family_name: this.personDetailsFormGroup.get('family_name')?.value ?? '',
                birth_sex: this.personDetailsFormGroup.get('birth_sex')?.value ?? '',
                preferred_name: this.personDetailsFormGroup.get('preferred_name')?.value ?? '',
                pronouns: this.personDetailsFormGroup.get('pronouns')?.value ?? '',
                date_of_birth: this.personDetailsFormGroup.get('date_of_birth')?.value ?? '',
                initials: `${this.personDetailsFormGroup.get('given_name')?.value?.charAt(0) || ''}${this.personDetailsFormGroup.get('family_name')?.value?.charAt(0) || ''}`.toUpperCase()
            }
        };

        this.updateClientRequest.send(clientData);
    }

    public static show(dialog: DialogService, data: Client) {
        return dialog.openDialog<DialogRef, Client>(EditClientGeneralInformationDialogComponent, {
            data
        });
    }
}
